import React, {
  Suspense,
  lazy,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import Cookies from "js-cookie";
import { getApiUrl } from "./components/agentConnector/Api";
import {
  appendBlock,
  createRootAgentSession,
  streamRootAgentMessage,
} from "./components/agentConnector/RootAgent";
import { getWidgetAgentToken } from "./components/agentConnector/widgetToken";
import ChatHistoryPanel from "./components/ChatHistoryPanel";
import LanguageToggle from "./components/LanguageToggle";
import LeadFormPanel from "./components/LeadFormPanel";
import MessagesArea from "./components/MessagesArea";
import useChatViewport from "./components/useChatViewport";
import {
  AvatarSpeechProvider,
  useAvatarSpeech,
} from "./components/tts/AvatarSpeechContext";
import { DEFAULT_AVATAR_ID, getAvatar } from "./components/avatar/avatars";
import { isSpeakable } from "./components/tts/speechText";

// Loaded the first time a visitor expands the chat: it carries the 3D avatar
// (three.js), the gsap transitions and the option panels, none of which the
// small chat window needs — so the widget a page downloads first stays small.
const ExpandedChatPanel = lazy(() =>
  import("./ExpandedChatPanel").then((m) => ({ default: m.ExpandedChatPanel })),
);

// ─── The visitor's history and language: cookies ──────────────────────────
// Each bot gets its own cookies so multiple bots on the same domain don't clash.
// `activeChat`, the conversation open now, and `chatHistory`, the last
// MAX_HISTORY conversation ids, newest first, are the visitor's only way back
// to a conversation; the conversations themselves live on the server, so one
// the bot owner deleted simply stops loading. `language` is the answer
// language the visitor picked with the header toggle.
const cookieKey = (botId, name) => `glopal_${botId}_${name}`;
const MAX_HISTORY = 20;
// Renewed on every write, so a visitor who keeps coming back keeps the history.
const cookieOptions = () => ({
  expires: 30,
  sameSite: "Lax",
  secure: window.location.protocol === "https:",
});

const readHistoryCookie = (botId) => {
  try {
    const ids = JSON.parse(Cookies.get(cookieKey(botId, "chatHistory")) || "[]");
    return Array.isArray(ids) ? ids.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
};

const writeHistoryCookie = (botId, ids) =>
  Cookies.set(
    cookieKey(botId, "chatHistory"),
    JSON.stringify(ids.slice(0, MAX_HISTORY)),
    cookieOptions(),
  );

// "en" or "zh", or null when the visitor has not picked one yet.
const readLanguageCookie = (botId) => {
  const saved = Cookies.get(cookieKey(botId, "language"));
  return saved === "en" || saved === "zh" ? saved : null;
};

// The bot's language setting ("en" / "zh-tw") as the toggle's "en" / "zh".
const toAnswerLanguage = (language) =>
  String(language || "en").startsWith("zh") ? "zh" : "en";

// ─── Conversations are recorded by the Root Agent ─────────────────────────
// The widget never saves: the Root Agent records each turn it answers into
// bot_tenant_conversations/{botId}, as the answer streams, even when the tab
// closes mid-answer. The widget only reads conversations back.

// ─── Helper: saved messages → chat bubbles ────────────────────────────────
// The saved id doubles as the message id, so a reply loaded from history can
// still be spoken and tracked by the avatar's voice.
const savedMessagesToChat = (saved) =>
  (saved || []).map((m) => {
    const meta = m.meta || {};
    return {
      id: m.id,
      type: m.role === "user" ? "user" : "bot",
      text: m.message,
      timestamp: new Date(m.timestamp),
      isFaqAgent: Boolean(meta.isFaqAgent),
      source: meta.source,
      webSources: meta.webSources,
      matchedFaqs: meta.matchedFaqs,
      videos: meta.videos,
    };
  });

// ─── Main component ────────────────────────────────────────────────────────
const ChatWidgetV2Inner = ({
  botId,
  botName = "Glo-pal Bot",
  primaryColor = "#E78A24",
  language = "en",
  avatarId = DEFAULT_AVATAR_ID,
  welcomeMessage = "Hi! How can I help you today?",
  stylePreset = "rounded",
  starterPrompts = [],
  className = "",
  // The embed's loader mounts the widget only when the visitor clicks its chat
  // bubble, so it starts open there.
  defaultOpen = false,
}) => {
  const { autoSpeak, toggleSpeak, stop: stopSpeaking } = useAvatarSpeech();
  // Which face and voice this bot has — the same avatar appears in the
  // collapsed header, on every reply bubble and in the expanded panel.
  const avatar = getAvatar(avatarId);
  const radius = stylePreset === "sharp" ? 0 : 24;
  // ── UI state ──────────────────────────────────────────────────────────
  const [chatOpen, setChatOpen] = useState(defaultOpen);
  const [menuSelection, setMenuSelection] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showNewChatConfirm, setShowNewChatConfirm] = useState(false);
  const viewport = useChatViewport(chatOpen && !isExpanded);
  const isNarrowChat = viewport.width <= 640;
  const isCompactChat = isNarrowChat || viewport.height <= 540;
  const chatEdge = isCompactChat ? 12 : 24;
  const headerButtonClass =
    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/15 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white";

  // ── Lead form state ───────────────────────────────────────────────────
  const [leadEmail, setLeadEmail] = useState("");
  const [leadMessage, setLeadMessage] = useState("");
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadError, setLeadError] = useState("");

  // ── Loading flags (separate per concern for clear UX feedback) ─────────
  const [isLoading, setIsLoading] = useState(false); // sending a message
  const [isLoadingHistory, setIsLoadingHistory] = useState(false); // fetching history list
  const [isLoadingChat, setIsLoadingChat] = useState(false); // loading a single session

  // ── Chat data ─────────────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState(uuidv4());
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");

  // chatHistory: array of session IDs stored in cookies
  // fetchedChatHistory: full session objects fetched from backend
  const [chatHistory, setChatHistory] = useState([]);
  const [fetchedChatHistory, setFetchedChatHistory] = useState([]);

  // Bumped once per completed bot reply, so auto-speak fires when a turn ends
  // rather than on every change to `messages` — loading a session from history
  // or re-rendering a streaming bubble must not trigger the voice.
  const [replyVersion, setReplyVersion] = useState(0);

  // The language the Root Agent answers in, "en" or "zh": the visitor's own
  // pick from the header toggle, else the bot's language setting. It goes with
  // every question, so switching mid-conversation changes the next answer.
  const [answerLanguage, setAnswerLanguage] = useState(
    () => readLanguageCookie(botId) || toAnswerLanguage(language),
  );

  // ── DOM refs ──────────────────────────────────────────────────────────
  const chatRef = useRef(null);
  const messagesEndRef = useRef(null);
  // The Root Agent's session shares the widget's sessionId; this records which
  // conversation it has been opened for, so it is opened once per conversation.
  const rootAgentSessionRef = useRef(null);

  // ── Derived style (primaryColor applied inline where Tailwind can't) ───
  const accentStyle = { color: primaryColor };
  const borderAccentStyle = { borderColor: primaryColor };

  // ─────────────────────────────────────────────────────────────────────
  // API helpers
  // ─────────────────────────────────────────────────────────────────────

  // Fetch the summaries of the conversations in the history cookie. Ids the
  // server no longer has (the bot owner deleted them) are dropped from the
  // cookie — except the open conversation, which the Root Agent records only
  // once its first answer has finished.
  const fetchMultipleSessions = useCallback(
    async (ids) => {
      if (!ids.length) {
        setFetchedChatHistory([]);
        return;
      }
      try {
        setIsLoadingHistory(true);
        const res = await axios.get(`${getApiUrl()}/${botId}/multiple`, {
          params: { session_ids: ids.join(",") },
        });
        if (!res.data.success) return;
        const sessions = res.data.sessions || [];
        setFetchedChatHistory(sessions);

        const found = new Set(sessions.map((s) => s.session_id));
        const open = Cookies.get(cookieKey(botId, "activeChat"));
        const kept = ids.filter((id) => found.has(id) || id === open);
        if (kept.length !== ids.length) {
          writeHistoryCookie(botId, kept);
          setChatHistory(kept);
        }
      } catch (err) {
        console.error("[ChatWidgetV2] Failed to fetch session list:", err);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [botId],
  );

  // Fetch messages for a single session and optionally apply them to state.
  // Resolves "ok", "missing" (the server does not have it) or "error".
  const loadSingleSession = useCallback(
    async (sid, applyMessages) => {
      try {
        setIsLoadingChat(true);
        const res = await axios.get(`${getApiUrl()}/${botId}/${sid}`);
        if (res.data.success && applyMessages) {
          applyMessages(savedMessagesToChat(res.data.messages));
        }
        return "ok";
      } catch (err) {
        console.error("[ChatWidgetV2] Failed to load session:", sid, err);
        return err.response?.status === 404 ? "missing" : "error";
      } finally {
        setIsLoadingChat(false);
      }
    },
    [botId],
  );

  // Puts a conversation at the front of the visitor's history and makes it the
  // open one. Rewriting both cookies also renews their expiry.
  const rememberSession = useCallback(
    (sid) => {
      Cookies.set(cookieKey(botId, "activeChat"), sid, cookieOptions());
      setChatHistory((prev) => {
        const updated = [sid, ...prev.filter((id) => id !== sid)].slice(
          0,
          MAX_HISTORY,
        );
        writeHistoryCookie(botId, updated);
        return prev[0] === sid ? prev : updated;
      });
    },
    [botId],
  );

  // ─────────────────────────────────────────────────────────────────────
  // Effects
  // ─────────────────────────────────────────────────────────────────────

  // Restore saved sessions from cookies on first mount
  // (the session list itself is fetched by the effect below, once
  //  chatHistory has been populated)
  useEffect(() => {
    if (!botId) return;

    setChatHistory(readHistoryCookie(botId));

    const activeChat = Cookies.get(cookieKey(botId, "activeChat"));
    if (activeChat) {
      setSessionId(activeChat);
      loadSingleSession(activeChat, setMessages).then((status) => {
        // Gone from the server: start over rather than keep adding to a
        // conversation nobody can see.
        if (status === "missing") {
          Cookies.remove(cookieKey(botId, "activeChat"));
          setSessionId(uuidv4());
        }
      });
    }
  }, [botId, loadSingleSession]); // re-run if botId changes (defensive)

  // Close widget when user clicks outside
  useEffect(() => {
    // composedPath, not e.target: in the embed the widget sits inside a Shadow
    // DOM, and a document listener sees every click in there as a click on the
    // shadow host — which would close the chat on any click inside it.
    const handleClickOutside = (e) => {
      if (chatRef.current && !e.composedPath().includes(chatRef.current)) {
        setChatOpen(false);
      }
    };
    if (chatOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [chatOpen]);

  // Scroll to bottom whenever messages change, or when the widget/panel opens
  // (chatOpen: small widget opened with pre-loaded session;
  //  isExpanded: expanded panel mounted with existing messages)
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatOpen, isExpanded]);

  // Show welcome message the first time the widget is opened
  useEffect(() => {
    if (chatOpen && messages.length === 0) {
      setMessages([
        {
          id: uuidv4(),
          type: "bot",
          text: welcomeMessage,
          timestamp: new Date(),
        },
      ]);
    }
  }, [chatOpen, messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh the session list when the stored list changes — on load, and when
  // a conversation is added to it or removed.
  useEffect(() => {
    fetchMultipleSessions(chatHistory);
  }, [chatHistory, fetchMultipleSessions]);

  // ── Auto-speak ────────────────────────────────────────────────────────
  // The avatar's voice is off while "AI video Q&A" is selected: the answer is
  // a video to watch, and reading its text aloud would talk over it.
  const speechEnabled = menuSelection !== "video FAQ";
  const speechEnabledRef = useRef(speechEnabled);
  speechEnabledRef.current = speechEnabled;

  // Latest values are read through refs so the effect below depends on
  // `replyVersion` alone: it must fire when a reply lands, and never merely
  // because the user flipped the switch or another render came through.
  const autoSpeakRef = useRef(autoSpeak);
  autoSpeakRef.current = autoSpeak;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const speakRef = useRef(toggleSpeak);
  speakRef.current = toggleSpeak;

  useEffect(() => {
    if (replyVersion === 0 || !autoSpeakRef.current || !speechEnabledRef.current)
      return;
    const last = messagesRef.current[messagesRef.current.length - 1];
    // Skips replies its voice has nothing to say for.
    if (last?.type === "bot" && isSpeakable(last)) {
      speakRef.current(last.id, last);
    }
  }, [replyVersion]);

  // Closing the widget silences the avatar — the provider stays mounted with
  // the widget, so playback would otherwise carry on behind a closed panel.
  useEffect(() => {
    if (!chatOpen) stopSpeaking();
  }, [chatOpen, stopSpeaking]);

  // Switching to the video menu also silences a reply already being read.
  useEffect(() => {
    if (!speechEnabled) stopSpeaking();
  }, [speechEnabled, stopSpeaking]);

  // ─────────────────────────────────────────────────────────────────────
  // User actions
  // ─────────────────────────────────────────────────────────────────────

  const handleNewChat = () => {
    // Require confirmation only when there's a real conversation
    if (messages.length > 1) {
      setShowNewChatConfirm(true);
    } else {
      confirmNewChat();
    }
  };

  // The conversation being left is already in the history cookie — it was
  // added when its first message was sent.
  const confirmNewChat = () => {
    stopSpeaking(); // don't carry the old conversation's voice into a new one
    const newSessionId = uuidv4();
    setSessionId(newSessionId);
    setMessages([
      {
        id: uuidv4(),
        type: "bot",
        text: welcomeMessage,
        timestamp: new Date(),
      },
    ]);
    // It becomes the open conversation once its first message is sent;
    // pointing the cookie at it now would leave it pointing at nothing.
    Cookies.remove(cookieKey(botId, "activeChat"));
    setInputMessage("");
    setShowHistory(false);
    setShowNewChatConfirm(false);
  };

  // Remembered per bot, so the visitor's next visit answers in it too. A reply
  // still being read aloud stops: the voice should not carry on past the switch.
  const handleLanguageChange = (next) => {
    stopSpeaking();
    setAnswerLanguage(next);
    Cookies.set(cookieKey(botId, "language"), next, cookieOptions());
  };

  const handleToggleHistory = async () => {
    const opening = !showHistory;
    setShowHistory(opening);

    // Refresh list when panel opens
    if (opening && chatHistory.length > 0) {
      await fetchMultipleSessions(chatHistory);
    }
  };

  const handleLoadFromHistory = async (sid) => {
    stopSpeaking();
    await loadSingleSession(sid, (loaded) => {
      setSessionId(sid);
      setMessages(loaded);
      setShowHistory(false);
      rememberSession(sid);
    });
  };

  // Removes the conversation from this visitor's history only: the bot owner
  // keeps it, and sees it in the dashboard's Conversations tab.
  const handleDeleteFromHistory = (sid) => {
    setChatHistory((prev) => {
      const updated = prev.filter((id) => id !== sid);
      writeHistoryCookie(botId, updated);
      return updated;
    });
    setFetchedChatHistory((prev) =>
      prev.filter((chat) => chat.session_id !== sid),
    );
    if (sid === sessionId) confirmNewChat();
  };

  // Shows an answer as it arrives: replaces the "Thinking..." placeholder with
  // one live bubble, updated in place. `extra` carries whatever source data is
  // known so far, so it survives every replacement instead of only landing on
  // the final message.
  const setBotBubble = (nextText, extra = {}) => {
    setMessages((prev) => {
      const withoutThinking = prev.filter((m) => m.text !== "Thinking...");
      const last = withoutThinking[withoutThinking.length - 1];
      const bubble = {
        // Carry the id across every replacement so the finished reply keeps
        // the identity the speak button and auto-speak track it by.
        id: last?.streaming ? last.id : uuidv4(),
        type: "bot",
        text: nextText,
        timestamp: new Date(),
        streaming: true,
        ...extra,
      };
      if (last?.streaming) {
        return [...withoutThinking.slice(0, -1), bubble];
      }
      return [...withoutThinking, bubble];
    });
  };

  // The live bubble becomes the finished reply. It keeps the language it was
  // asked for in, so it is read aloud in that voice after the toggle moves on.
  const finishBotBubble = (answer) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.streaming
          ? {
              ...m,
              streaming: false,
              faqStageOnly: false,
              language: answer.language,
              text: answer.text,
              isFaqAgent: Boolean(answer.isFaqAgent),
              source: answer.source,
              webSources: answer.webSources,
              matchedFaqs: answer.matchedFaqs,
              videos: answer.videos,
            }
          : m,
      ),
    );
  };

  // Every visitor talks to the Root Agent as this bot, with the token PIMS's
  // backend issues for this site (agentConnector/widgetToken.js) — also in the
  // Bot Platform preview and on PIMS's own pages, so they behave and are
  // recorded exactly like a customer's site.
  const agentUserId = `widget_${botId}`;

  // The Root Agent answers every menu, the FAQ one included, so visitors get
  // all of its skills. Its session shares the widget's sessionId, so the
  // agent's session and the recorded conversation are the same conversation.
  // Video questions ask it for video answers.
  const askRootAgent = async (text, sid, answer) => {
    const token = await getWidgetAgentToken(botId);
    if (rootAgentSessionRef.current !== sid) {
      await createRootAgentSession(token, agentUserId, sid);
      rootAgentSessionRef.current = sid;
    }

    let failed = null;
    await streamRootAgentMessage(
      token,
      agentUserId,
      sid,
      text,
      {
        onStageChange: () => {},
        onBlock: (block) => {
          answer.text = appendBlock(answer.text, block);
          setBotBubble(answer.text);
        },
        // FAQ-path answers cite these as [1], [2]... — rendered with the FAQ
        // renderer, which turns the markers into links.
        onSources: (sources) => {
          answer.isFaqAgent = true;
          answer.source = "web_search";
          answer.webSources = sources;
        },
        onVideos: (videos) => {
          answer.videos = videos;
        },
        onWorker: (worker) => {
          answer.worker = worker;
        },
        onDone: () => {},
        onError: (err) => {
          failed = err;
        },
      },
      answer.language,
      menuSelection === "video FAQ",
      // Concrete values every turn: stateDelta merges, so an omitted setting
      // would keep the previous turn's. These are the backend's own defaults.
      { model: "gemini-3.5-flash-lite", thinking: "balanced", verbosity: "medium" },
    );
    if (failed) throw failed;
  };

  const handleSendMessage = async (overrideText) => {
    const text = (
      typeof overrideText === "string" ? overrideText : inputMessage
    ).trim();
    if (!text || isLoading || !botId) return;

    stopSpeaking();
    setIsLoading(true);
    const currentSessionId = sessionId;

    const userMessage = {
      id: uuidv4(),
      type: "user",
      text,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");

    const thinking = {
      id: uuidv4(),
      type: "bot",
      text: "Thinking...",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, thinking]);

    // The Root Agent records the turn; the widget only keeps the conversation
    // in this visitor's history.
    rememberSession(currentSessionId);
    const answer = { text: "", language: answerLanguage };

    try {
      await askRootAgent(text, currentSessionId, answer);
      if (!answer.text) throw new Error("The agent returned no answer");

      finishBotBubble(answer);
      setReplyVersion((v) => v + 1);
    } catch (err) {
      console.error("[ChatWidgetV2] Failed to get an answer:", err);
      setMessages((prev) => [
        ...prev.filter((m) => m.text !== "Thinking..." && !m.streaming),
        {
          id: uuidv4(),
          type: "bot",
          text: "Sorry, there was an error processing your message. Please try again.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadEmail.trim()) return;

    setLeadSubmitting(true);
    setLeadError("");
    try {
      await axios.post(
        `${getApiUrl()}/${botId}/leads`,
        {
          email: leadEmail.trim(),
          message: leadMessage.trim(),
          session_id: sessionId,
        },
      );
      setLeadSubmitted(true);
      setLeadEmail("");
      setLeadMessage("");
    } catch (err) {
      console.error("[ChatWidgetV2] Failed to submit lead:", err);
      setLeadError("Failed to submit. Please try again.");
    } finally {
      setLeadSubmitting(false);
    }
  };

  const openLeadForm = () => {
    setShowLeadForm(true);
    setShowHistory(false);
    setLeadSubmitted(false);
    setLeadError("");
  };

  return (
    <div
      ref={chatRef}
      className={className}
      style={{
        position: "fixed",
        bottom: "max(24px, env(safe-area-inset-bottom, 0px))",
        right: "max(24px, env(safe-area-inset-right, 0px))",
        zIndex: 9999,
      }}
    >
      {/* Expanded backdrop */}
      {chatOpen && isExpanded && (
        <div
          onClick={() => setIsExpanded(false)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 1)",
            zIndex: 10000,
          }}
        />
      )}

      {chatOpen && isExpanded && (
        <Suspense fallback={null}>
        <ExpandedChatPanel
          onClose={() => setIsExpanded(false)}
          onNewChat={confirmNewChat}
          botName={botName}
          botId={botId}
          sessionId={sessionId}
          avatarId={avatarId}
          language={answerLanguage}
          onLanguageChange={handleLanguageChange}
          messages={messages}
          starterPrompts={starterPrompts}
          stylePreset={stylePreset}
          primaryColor={primaryColor}
          radius={radius}
          isLoadingChat={isLoadingChat}
          messagesEndRef={messagesEndRef}
          onSendMessage={handleSendMessage}
          inputMessage={inputMessage}
          onInputChange={setInputMessage}
          onKeyDown={handleKeyPress}
          isLoading={isLoading}
          borderAccentStyle={borderAccentStyle}
          isLoadingHistory={isLoadingHistory}
          fetchedChatHistory={fetchedChatHistory}
          accentStyle={accentStyle}
          onLoadFromHistory={handleLoadFromHistory}
          onDeleteFromHistory={handleDeleteFromHistory}
          menuSelection={menuSelection}
          onMenuSelection={setMenuSelection}
          speechEnabled={speechEnabled}
        />
        </Suspense>
      )}

      {/* ── Chat Window ─────────────────────────────────────────── */}
      {chatOpen && !isExpanded && (
        <div
          className="fixed box-border flex items-end justify-end pointer-events-none"
          style={{
            top: viewport.top,
            left: viewport.left,
            width: viewport.width,
            height: viewport.height,
            paddingTop: `max(${chatEdge}px, env(safe-area-inset-top, 0px))`,
            paddingRight: `max(${chatEdge}px, env(safe-area-inset-right, 0px))`,
            paddingLeft: `max(${chatEdge}px, env(safe-area-inset-left, 0px))`,
            // Reserve the launcher and its gap only in floating desktop mode.
            paddingBottom: isCompactChat
              ? "max(12px, env(safe-area-inset-bottom, 0px))"
              : "calc(max(24px, env(safe-area-inset-bottom, 0px)) + 80px)",
          }}
        >
          <div
            className="relative box-border flex min-h-0 min-w-0 flex-col items-center overflow-hidden pointer-events-auto shadow-[0_20px_60px_rgba(0,0,0,0.2)] border border-white/20"
            style={{
              width: isNarrowChat ? "100%" : 500,
              height: isCompactChat ? "100%" : 700,
              maxWidth: "100%",
              maxHeight: "100%",
              backgroundColor: `${primaryColor}BF`,
              borderRadius: radius,
            }}
          >
            {/* Header */}
            <div className="text-white px-2 py-3 flex w-full shrink-0 items-center gap-1">
              {/* Bot avatar + name */}
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <div className="w-9 h-9 shrink-0 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                  <img
                    src={avatar.thumb}
                    alt={`${botName} avatar`}
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
                <h3 className="min-w-0 truncate font-semibold text-sm" title={botName}>
                  {botName}
                </h3>
              </div>

              {/* Action buttons */}
              <div className="flex shrink-0 items-center">
                <div className="mr-1">
                  <LanguageToggle
                    language={answerLanguage}
                    onChange={handleLanguageChange}
                  />
                </div>
                <button
                  onClick={() => {
                    setShowLeadForm(false);
                    setShowHistory(false);
                    handleNewChat();
                  }}
                  disabled={isLoading}
                  title="Start new chat"
                  className={`${headerButtonClass} disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    setShowLeadForm(false);
                    handleToggleHistory();
                  }}
                  title="View chat history"
                  className={headerButtonClass}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() =>
                    showLeadForm ? setShowLeadForm(false) : openLeadForm()
                  }
                  title={showLeadForm ? "Back to Chat" : "Contact us"}
                  className={headerButtonClass}
                  style={{ opacity: showLeadForm ? 0.6 : 1 }}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setIsExpanded((v) => !v)}
                  title={isExpanded ? "Collapse" : "Expand"}
                  className={headerButtonClass}
                >
                  {isExpanded ? (
                    /* Collapse: two inward-pointing arrows (top-left + bottom-right) */
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7"
                      />
                    </svg>
                  ) : (
                    /* Expand: two outward-pointing arrows (top-right + bottom-left) */
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {/* Close button */}
              <button
                onClick={() => setChatOpen(false)}
                className={headerButtonClass}
                title="Close chat"
                aria-label="Close chat"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content: lead form / history panel / messages area */}
            <div className="flex min-h-0 w-full flex-1 flex-col items-center">
              {showLeadForm ? (
                <LeadFormPanel
                  radius={radius}
                  primaryColor={primaryColor}
                  leadSubmitted={leadSubmitted}
                  leadEmail={leadEmail}
                  leadMessage={leadMessage}
                  leadSubmitting={leadSubmitting}
                  leadError={leadError}
                  onEmailChange={setLeadEmail}
                  onMessageChange={setLeadMessage}
                  onSubmit={handleLeadSubmit}
                  onClose={() => setShowLeadForm(false)}
                />
              ) : showHistory ? (
                <ChatHistoryPanel
                  isLoadingHistory={isLoadingHistory}
                  isLoadingChat={isLoadingChat}
                  fetchedChatHistory={fetchedChatHistory}
                  currentSessionId={sessionId}
                  accentStyle={accentStyle}
                  primaryColor={primaryColor}
                  onClose={() => setShowHistory(false)}
                  onLoad={handleLoadFromHistory}
                  onDelete={handleDeleteFromHistory}
                />
              ) : (
                <MessagesArea
                  messages={messages}
                  avatarThumb={avatar.thumb}
                  starterPrompts={starterPrompts}
                  stylePreset={stylePreset}
                  primaryColor={primaryColor}
                  radius={radius}
                  isLoadingChat={isLoadingChat}
                  messagesEndRef={messagesEndRef}
                  onSendMessage={handleSendMessage}
                  isExpanded={isExpanded}
                  language={answerLanguage}
                  speechEnabled={speechEnabled}
                />
              )}
            </div>

            {/* Input area — hidden while history or lead form is open */}
            {!showHistory && !showLeadForm && (
              <div className={`w-full shrink-0 p-3 ${isCompactChat ? "" : "pb-6"}`}>
                <div className="flex items-center space-x-2">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Type your message..."
                    rows={1}
                    className={`min-w-0 flex-1 resize-none border text-gray-700 placeholder:text-white/80 px-3 py-2 ${isCompactChat ? "text-base" : "text-sm"} focus:outline-none focus:ring-2 focus:border-transparent max-h-24 bg-white/50`}
                    style={{
                      ...borderAccentStyle,
                      borderRadius: radius,
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="shrink-0 hover:scale-105 border border-white disabled:cursor-not-allowed text-white p-2 transition-transform"
                    aria-label="Send message"
                    style={{ borderRadius: radius }}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* New-chat confirmation modal */}
            {showNewChatConfirm && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50 rounded-lg">
                <div className="bg-white rounded-lg p-6 w-full max-w-sm max-h-full overflow-y-auto shadow-xl">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Start New Chat?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Your current conversation will be saved to history.
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setShowNewChatConfirm(false)}
                      className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmNewChat}
                      className="px-4 py-2 text-sm text-white font-semibold rounded-lg transition-colors"
                      style={{
                        backgroundColor: primaryColor,
                      }}
                    >
                      Start New Chat
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Toggle button ────────────────────────────────────────── */}
      {!(chatOpen && !isExpanded && isCompactChat) && (
        <button
          onClick={() => setChatOpen(!chatOpen)}
          className="shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 hover:-translate-y-2 hover:-translate-x-2 w-[70px] h-[70px] rounded-full flex items-center justify-center text-white"
          style={{ backgroundColor: primaryColor }}
          title={chatOpen ? "Close chat" : `Chat with ${botName}`}
        >
          {chatOpen ? (
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
          )}
        </button>
      )}
    </div>
  );
};

// The avatar's voice is provided around the whole widget, so the speak buttons
// in MessagesArea and the lip-sync in the avatar share one audio pipeline —
// and so playback survives collapsing the expanded panel.
export const ChatWidgetV2 = (props) => (
  <AvatarSpeechProvider
    voice={getAvatar(props.avatarId).voice}
    botId={props.botId}
  >
    <ChatWidgetV2Inner {...props} />
  </AvatarSpeechProvider>
);

export default ChatWidgetV2;
